import { SystemLanguageEnum } from "~/types/const";
import { localStorageLanguageCache } from './localStorageCache';
import { useRuntimeConfig } from '#app'
import { normalizeLanguageJson, mergeLanguageJson } from './mergeLanguageJson'

declare const __DOCLINGO_PUBLIC_API_URL__: string

const getFallbackApiBaseUrl = () => {
    return (typeof __DOCLINGO_PUBLIC_API_URL__ === 'string' ? __DOCLINGO_PUBLIC_API_URL__ : '')
        || process.env.NUXT_PUBLIC_API_URL
        || process.env.NUXT_API_URL
        || process.env.FOREIGN_API_URL
        || process.env.DOMESTIC_API_URL
        || ''
}

const getApiBaseUrl = () => {
    try {
        return useRuntimeConfig().public.apiUrl || getFallbackApiBaseUrl()
    } catch {
        return getFallbackApiBaseUrl()
    }
}
const serverCodeMap = {
    [SystemLanguageEnum.ZH]: 'zh-cn',
    [SystemLanguageEnum.ZH_TW]:"zh-tw",
    [SystemLanguageEnum.EN]:'en',
    [SystemLanguageEnum.AR]:'ar',
    [SystemLanguageEnum.FR]:'fr',
    [SystemLanguageEnum.DE]:'de',
    [SystemLanguageEnum.JA]:'ja',
    [SystemLanguageEnum.KO]:'ko',
    [SystemLanguageEnum.HI]:'hi',
    [SystemLanguageEnum.ID]:'id',
    [SystemLanguageEnum.ITB]:'itb',
    [SystemLanguageEnum.UK]:'uk',
}
export default serverCodeMap;

const localJsonLoaders: Record<string, () => Promise<any>> = {
    'zh': () => import('./json/zh.json'),
    'zh-cn': () => import('./json/zh.json'),
    'en': () => import('./json/en.json'),
    'ja': () => import('./json/ja.json'),
    'zh-tw': () => import('./json/tw.json'),
    'ko': () => import('./json/ko.json'),
    'hi': () => import('./json/hi.json'),
    'ar': () => import('./json/ar.json'),
    'de': () => import('./json/de.json'),
    'fr': () => import('./json/fr.json'),
    'id': () => import('./json/id.json'),
    'itb': () => import('./json/itb.json'),
    'uk': () => import('./json/uk.json'),
};

function getLocaleCacheCode(locale: string) {
    return (serverCodeMap as Record<string, string>)[locale] || locale.toLowerCase();
}

async function getLocalBaseLanguageJson(locale: string) {
    const loader = localJsonLoaders[getLocaleCacheCode(locale)];
    if (!loader) {
        return {};
    }
    const json = await loader();
    return normalizeLanguageJson(json);
}

export async function getServerLanguageBySave(locale: string, json: any) {
    const baseJson = normalizeLanguageJson(json);
    let oldJson = {
        ...baseJson
    }
    
    if (import.meta.server) {
        // // 服务端渲染：检查localStorage缓存
        // const cached = localStorageLanguageCache.getLanguageCache(locale);
        // if (cached) {
        //     console.log(`Using localStorage cached language data for ${locale}, version: ${cached.version}`);
        //     return cached.data;
        // }

        // 获取本地版本号（仅在开发环境）
        let localVersion = '';
        const cached = localStorageLanguageCache.getLanguageCache(locale);
        if (cached) {
            console.log(`Using localStorage cached language data for ${locale}, version: ${cached.version}`);
            localVersion = cached.version;
        }
        // try {
        //     const rootDir = process.cwd().includes('.output') 
        //         ? path.resolve(process.cwd(), '..') 
        //         : process.cwd();
        //     const versionPath = path.resolve(rootDir, 'i18n', 'json/version.json');
        //     const versionJson = await fs.readFileSync(versionPath, 'utf8');
        //     const zh_version = JSON.parse(versionJson);
        //     localVersion = zh_version['version'];
        // } catch (error) {
        //     console.log('Could not read local version file, using empty version');
        // }
        // 请求服务器版本信息
        let res_version: any;
        try {
            res_version = await $fetch(`${getApiBaseUrl()}/api/user/config/homepage`, {
                method: 'GET',
                headers: {
                    'accept': "application/json",
                    'Content-Type': "application/json",
                    'deviceId': "3daf2da89e176c59aebb1034ffd5d656",
                    'internationalCode': locale,
                    'clientType': "web",
                    'appSource': "web",
                },
            });
        } catch (error) {
            console.error('Failed to fetch version from server:', error);
            return oldJson;
        }

        if (res_version.code === '1') {
            const serverVersion = res_version.data.multiLangVersionByBiz;
            console.log(`Version check - Local: ${localVersion}, Server: ${serverVersion}, Locale: ${locale}`);
            
            // 检查是否需要更新（版本不一致或缓存不存在）
            if (localVersion !== serverVersion) {
                try {
                    const res: any = await $fetch(`${getApiBaseUrl()}/api/user/language/config/clientGetLanguageConfig`, {
                        method: 'GET',
                        headers: {
                            'accept': "application/json",
                            'Content-Type': "application/json",
                            'deviceId': "3daf2da89e176c59aebb1034ffd5d656",
                            'internationalCode': locale,
                            'clientType': "web",
                            'appSource': "web",
                        },
                        query: {
                            bizModelFlag: true
                        }
                    });

                    if (res.code === '1') {
                        const { chineseStr, englishStr, japaneseStr, chineseTwStr, koreanStr, versionNo, hindiStr, arStr, deStr, frStr, idStr, itbStr, ukStr } = res.data;
                        
                        // 根据语言类型解析并缓存到localStorage
                        const languageMap: { [key: string]: string } = {
                            'zh-cn': chineseStr,
                            'en': englishStr,
                            'ja': japaneseStr,
                            'zh-tw': chineseTwStr,
                            'ko': koreanStr,
                            'hi': hindiStr,
                            'ar': arStr,
                            'de': deStr,
                            'fr': frStr,
                            'id': idStr,
                            'itb': itbStr,
                            'uk': ukStr
                        };

                        // 缓存所有语言数据到localStorage
                        for (const [langCode, langStr] of Object.entries(languageMap)) {
                            if (langStr) {
                                try {
                                    const targetBaseJson = await getLocalBaseLanguageJson(langCode);
                                    const newJson = mergeLanguageJson(targetBaseJson, JSON.parse(langStr));
                                    localStorageLanguageCache.setLanguageCache(langCode, newJson, versionNo);
                                    
                                    // 如果是当前请求的语言，更新返回值
                                    if (langCode === locale) {
                                        oldJson = newJson;
                                    }
                                } catch (parseError) {
                                    console.error(`Failed to parse language data for ${langCode}:`, parseError);
                                }
                            }
                        }

                        // 设置全局版本号
                        localStorageLanguageCache.setGlobalVersion(versionNo);
                        console.log(`Successfully updated localStorage language cache for ${locale}, version: ${versionNo}`);
                    }
                } catch (error) {
                    console.error('Failed to fetch language config from server:', error);
                }
            } else {
                const cached = localStorageLanguageCache.getLanguageCache(locale);
                if (cached) {
                    const mergedJson = mergeLanguageJson(baseJson, cached.data);
                    if (Object.keys(mergedJson).length !== Object.keys(cached.data || {}).length) {
                        localStorageLanguageCache.setLanguageCache(locale, mergedJson, cached.version);
                    }
                    return mergedJson;
                }
            }
        }
    }
    
    return oldJson;
}

export async function getLocalLanguageBySave(json: any, locale: string) {
    const baseJson = await getLocalBaseLanguageJson(locale);
    let oldJson = mergeLanguageJson(baseJson, json);
    let localVersion = localStorage.getItem(`lang_version`);
    const localeCacheCode = getLocaleCacheCode(locale);
    
    // 请求服务器版本信息
    let res_version: any;
    try {
        res_version = await $fetch(`${getApiBaseUrl()}/api/user/config/homepage`, {
            method: 'GET',
            headers: {
                'accept': "application/json",
                'Content-Type': "application/json",
                'deviceId': "3daf2da89e176c59aebb1034ffd5d656",
                'internationalCode': locale,
                'clientType': "web",
                'appSource': "web",
            },
        });
    } catch (error) {
        console.error('Failed to fetch version from server:', error);
        return oldJson;
    }

    if (res_version.code === '1') {
        const serverVersion = res_version.data.multiLangVersionByBiz;
        console.log(`Version check - Local: ${localVersion}, Server: ${serverVersion}, Locale: ${locale}`);
        
        // 检查是否需要更新（版本不一致或缓存不存在）
        if (localVersion !== serverVersion) {
            try {
                const res: any = await $fetch(`${getApiBaseUrl()}/api/user/language/config/clientGetLanguageConfig`, {
                    method: 'GET',
                    headers: {
                        'accept': "application/json",
                        'Content-Type': "application/json",
                        'deviceId': "3daf2da89e176c59aebb1034ffd5d656",
                        'internationalCode': locale,
                        'clientType': "web",
                        'appSource': "web",
                    },
                    query: {
                        bizModelFlag: true
                    }
                });

                if (res.code === '1') {
                    const { chineseStr, englishStr, japaneseStr, chineseTwStr, koreanStr, versionNo, hindiStr, arStr, deStr, frStr, idStr, itbStr, ukStr } = res.data;
                    
                    // 根据语言类型解析并缓存到localStorage
                    const languageMap: { [key: string]: string } = {
                        'zh-cn': chineseStr,
                        'en': englishStr,
                        'ja': japaneseStr,
                        'zh-tw': chineseTwStr,
                        'ko': koreanStr,
                        'hi': hindiStr,
                        'ar': arStr,
                        'de': deStr,
                        'fr': frStr,
                        'id': idStr,
                        'itb': itbStr,
                        'uk': ukStr
                    };

                    // 缓存所有语言数据到localStorage
                    for (const [langCode, langStr] of Object.entries(languageMap)) {
                        if (langStr) {
                            try {
                                const targetBaseJson = await getLocalBaseLanguageJson(langCode);
                                const newJson = mergeLanguageJson(targetBaseJson, JSON.parse(langStr));
                                localStorage.setItem(`lang_${langCode}`, JSON.stringify(newJson));

                                
                                // 如果是当前请求的语言，更新返回值
                                if (langCode === localeCacheCode) {
                                    oldJson = newJson;
                                }
                            } catch (parseError) {
                                console.error(`Failed to parse language data for ${langCode}:`, parseError);
                            }
                        }
                    }
                    // 设置全局版本号
                    localStorage.setItem(`lang_version`, versionNo);
                    console.log(`Successfully updated localStorage language cache for ${locale}, version: ${versionNo}`);
                }
            } catch (error) {
                console.error('Failed to fetch language config from server:', error);
            }
        } else {
            console.log(`Language data is up to date for ${locale}`);
            localStorage.setItem(`lang_${localeCacheCode}`, JSON.stringify(oldJson));
        }
    }
    return oldJson
}
